import React from 'react';
import { format, parseISO } from 'date-fns';
import Icon from '../../../components/AppIcon';

const TimelineBrowser = ({ assessments, selectedAssessments, onAssessmentSelect }) => {
  const getAssessmentTypeLabel = (type) => {
    const labels = {
      'roi_calculator': 'ROI Calculator',
      'data_maturity': 'Data Maturity Assessment'
    };
    return labels?.[type] || type;
  };

  const getAssessmentIcon = (type) => {
    const icons = {
      'roi_calculator': 'DollarSign',
      'data_maturity': 'BarChart3'
    };
    return icons?.[type] || 'FileText';
  };

  const getTypeColor = (type) => {
    const colors = {
      'roi_calculator': 'bg-green-50 text-green-700 border-green-200',
      'data_maturity': 'bg-blue-50 text-blue-700 border-blue-200'
    };
    return colors?.[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Date not available';
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };

  if (!assessments || assessments?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-8 mb-8">
        <div className="text-center py-12">
          <Icon name="FileText" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No assessments found</p>
          <p className="text-sm text-gray-400 mt-2">Complete an assessment to see it here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
          <Icon name="Clock" size={20} className="text-purple-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Assessment Timeline</h3>
          <p className="text-sm text-gray-500">Browse all completed assessments chronologically</p>
        </div>
      </div>

      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1FB5A5] to-gray-200" />

        {/* Timeline Items */}
        <div className="space-y-6">
          {assessments?.map((assessment, index) => {
            const isSelected = selectedAssessments?.includes(assessment?.id);

            return (
              <div key={assessment?.id} className="relative pl-16">
                {/* Timeline Dot */}
                <div
                  className={`absolute left-3 w-6 h-6 rounded-full border-4 ${
                    isSelected
                      ? 'bg-[#1FB5A5] border-[#1FB5A5]'
                      : 'bg-white border-gray-300'
                  } transition-all duration-300`}
                >
                  {isSelected && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon name="Check" size={12} className="text-white" />
                    </div>
                  )}
                </div>

                {/* Assessment Card */}
                <div
                  onClick={() => onAssessmentSelect(assessment?.id)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                    isSelected
                      ? 'border-[#1FB5A5] bg-[#1FB5A5]/5 shadow-md'
                      : 'border-gray-200 hover:border-[#1FB5A5]/50 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#1FB5A5] to-[#002B45] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name={getAssessmentIcon(assessment?.assessmentType)} size={24} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#002B45] mb-1">{assessment?.title}</h4>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getTypeColor(assessment?.assessmentType)}`}>
                            {getAssessmentTypeLabel(assessment?.assessmentType)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={12} />
                            {formatDate(assessment?.createdAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Clock" size={12} />
                            {formatTime(assessment?.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex items-center gap-2 text-[#1FB5A5]">
                        <Icon name="CheckCircle" size={20} />
                        <span className="text-sm font-medium">Selected</span>
                      </div>
                    )}
                  </div>

                  {assessment?.results && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {assessment?.results?.overallScore && (
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 mb-1">Overall Score</p>
                            <p className="text-lg font-bold text-[#1FB5A5]">{assessment?.results?.overallScore}</p>
                          </div>
                        )}
                        {assessment?.results?.maturityLevel && (
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 mb-1">Maturity Level</p>
                            <p className="text-sm font-semibold text-[#002B45]">{assessment?.results?.maturityLevel}</p>
                          </div>
                        )}
                        {assessment?.results?.totalROI && (
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 mb-1">Total ROI</p>
                            <p className="text-sm font-semibold text-green-600">${assessment?.results?.totalROI?.toLocaleString()}</p>
                          </div>
                        )}
                        {assessment?.results?.paybackPeriod && (
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500 mb-1">Payback Period</p>
                            <p className="text-sm font-semibold text-[#002B45]">{assessment?.results?.paybackPeriod} months</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">Compare Assessments</p>
            <p className="text-xs text-blue-700">
              Select 2-3 assessments from the timeline to enable side-by-side comparison and see your improvement over time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineBrowser;